import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import { bindFormFields } from './formFields';
import { usePersonPicker } from './personPicker';
import { useValidationMessage } from '../i18n/validationMessages';
import type { InlineField } from './gridInlineEdit';

/**
 * Setting one set of values on MANY records at once — the Sammelbearbeitung
 * behind the grids' checkbox selection. It offers exactly the fields a grid
 * edits in place ({@link InlineField}), through the same {@link bindFormFields}
 * the record forms use.
 *
 * Every field is opted into by hand; nothing is written for an unticked one. An
 * empty value is REFUSED rather than treated as "clear it": a tick and an empty
 * box is what an interrupted edit looks like, and twenty Datierungen are gone
 * before anyone notices. Clearing is its own tick.
 */

export interface BulkIssue {
    field: string;
    code: string;
    count: number;
}

export interface GridBulkEditDialogProps {
    open: boolean;
    count: number;
    fields: readonly InlineField[];
    /** The i18n id of a field's label, e.g. `labelIdsFrom('find.field')`. */
    labelId: (name: string) => string;
    /** Titles the dialog and counts the records ("23 Funde bearbeiten"). */
    titleId: string;
    /**
     * Checks what the values would do, before anything is written. The grid
     * owns this: it has the schema and the records, this dialog has neither.
     */
    validate: (values: Record<string, string>) => BulkIssue[];
    onCancel: () => void;
    onApply: (values: Record<string, string>) => Promise<void>;
}

export default function GridBulkEditDialog({
    open,
    count,
    fields,
    labelId,
    titleId,
    validate,
    onCancel,
    onApply
}: GridBulkEditDialogProps) {
    const intl = useIntl();
    const validationMessage = useValidationMessage();
    /** The fields ticked to change, and those ticked to be emptied. */
    const [touched, setTouched] = useState<string[]>([]);
    const [cleared, setCleared] = useState<string[]>([]);
    const [issues, setIssues] = useState<BulkIssue[]>([]);
    const [missingValue, setMissingValue] = useState(false);
    const [busy, setBusy] = useState(false);

    const empty = useMemo(
        () => Object.fromEntries(fields.map((spec) => [spec.field, ''])),
        [fields]
    );
    // Reopening starts over: remembering the last run would apply yesterday's
    // Boxnummer to today's selection on a stray double-click. The caller
    // REMOUNTS this dialog per opening (see useGridBulkEdit's key).
    const {
        register,
        control,
        getValues,
        formState: { errors }
    } = useForm<Record<string, string>>({ defaultValues: empty });

    const personPicker = usePersonPicker();
    const {
        text,
        richText,
        suggest,
        multiSelectJoined,
        person,
        personMultiJoined
    } = bindFormFields<Record<string, string>>({
        intl,
        register,
        control,
        errors,
        validationMessage,
        labelId
    });

    // The SAME renderer the record form gives the field, picked by the editor
    // its cell uses; chips in the grid are chips here, over ', '-joined text.
    const fieldInput = (spec: InlineField) => {
        const editor = spec.editor ?? { kind: 'text' as const };
        switch (editor.kind) {
            case 'suggest':
                return suggest(spec.field, editor.options);
            case 'multi':
                return multiSelectJoined(spec.field, editor.options);
            case 'person':
                // A person is PICKED, never typed: the same value goes to a
                // whole selection, so a free-text name past the accounts would
                // not be one second spelling of somebody but thirty.
                return editor.multiple
                    ? personMultiJoined(spec.field, personPicker, {
                          leads: editor.leads
                      })
                    : person(spec.field, personPicker, {
                          leads: editor.leads
                      });
            case 'multiline':
                return richText(spec.field);
            case 'text':
                return text(spec.field);
        }
        /**
         * Exhaustive on purpose: an editor kind added to gridInlineEdit and
         * forgotten here fails to COMPILE instead of silently degrading to a
         * plain text box. At runtime it still falls back to text.
         */
        const unhandled: never = editor;
        void unhandled;
        return text(spec.field);
    };

    const toggle = (list: string[], field: string) =>
        list.includes(field)
            ? list.filter((entry) => entry !== field)
            : [...list, field];

    /** What would be written: the ticked fields, emptied ones as ''. */
    function pickedValues(): Record<string, string> {
        const all = getValues();
        return Object.fromEntries(
            touched.map((field) => [
                field,
                cleared.includes(field) ? '' : (all[field] ?? '').trim()
            ])
        );
    }

    async function handleApply() {
        const values = pickedValues();
        // A ticked field with nothing in it and no "empty this" — the mistake
        // this dialog exists to catch.
        const blank = touched.some(
            (field) => !cleared.includes(field) && values[field] === ''
        );
        setMissingValue(blank);
        if (blank) {
            return;
        }
        const found = validate(values);
        setIssues(found);
        if (found.length > 0) {
            return;
        }
        setBusy(true);
        try {
            await onApply(values);
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>
                <FormattedMessage id={titleId} values={{ count }} />
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    <FormattedMessage id="grid.bulkEdit.pick" />
                </DialogContentText>
                <Stack sx={{ gap: 1 }}>
                    {fields.map((spec) => {
                        const on = touched.includes(spec.field);
                        const clear = cleared.includes(spec.field);
                        return (
                            <Box key={spec.field}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={on}
                                            onChange={() => {
                                                setTouched((list) =>
                                                    toggle(list, spec.field)
                                                );
                                                setMissingValue(false);
                                                setIssues([]);
                                            }}
                                        />
                                    }
                                    label={intl.formatMessage({
                                        id: labelId(spec.field)
                                    })}
                                />
                                {on && (
                                    <Box sx={{ pl: 4, pb: 1 }}>
                                        {!clear && fieldInput(spec)}
                                        <FormControlLabel
                                            sx={{ mt: clear ? 0 : 0.5 }}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={clear}
                                                    onChange={() => {
                                                        setCleared((list) =>
                                                            toggle(
                                                                list,
                                                                spec.field
                                                            )
                                                        );
                                                        setMissingValue(false);
                                                    }}
                                                />
                                            }
                                            label={
                                                <FormattedMessage id="grid.bulkEdit.clear" />
                                            }
                                        />
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
                {missingValue && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <FormattedMessage id="grid.bulkEdit.needsValue" />
                    </Alert>
                )}
                {issues.map((issue) => (
                    <Alert key={issue.field} severity="error" sx={{ mt: 2 }}>
                        <FormattedMessage
                            id="grid.bulkEdit.blocked"
                            values={{
                                count: issue.count,
                                total: count,
                                field: intl.formatMessage({
                                    id: labelId(issue.field)
                                }),
                                reason: validationMessage(issue.code)
                            }}
                        />
                    </Alert>
                ))}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
                <Box sx={{ color: 'text.secondary', fontSize: 14 }}>
                    <FormattedMessage
                        id="grid.bulkEdit.summary"
                        values={{ fields: touched.length, count }}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={busy}
                    >
                        <FormattedMessage id="common.cancel" />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleApply}
                        disabled={busy || touched.length === 0}
                        startIcon={
                            busy ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : undefined
                        }
                    >
                        <FormattedMessage id="grid.bulkEdit.apply" />
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
